import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { me, getDraft, saveDraft, publish, changePassword, clearToken, getToken, listGiftCards, adjustGiftCard, listUsers, resetUserPassword, getActivityLog } from './adminApi';
import { locations as defaultLocations, products as defaultProducts, images as defaultImages, faqCategories as defFaqs, blogPosts as defPosts, cafe as cafeData, about as aboutData, secretRecipe as recipeData, specials as specialsData, customCakes as defaultCustomCakes } from '../mock/mock';
import { menuSections as defMenuSections, menuHours as defMenuHours, menuLegend as defMenuLegend, takeawaySpecials as defTakeaway } from '../mock/menuData';
import DragDropImage from './DragDropImage';
import logoCream from '../assets/logo-cream.png';

const TABS = ['Home Page', 'Page Text', 'About', 'Products', 'Pickup', 'Menu', 'Our Café', 'Stockists', 'Catering', 'Events', 'Jobs', 'Gift Voucher - Orders', 'Blog', 'FAQ', 'Account'];

const DEFAULT_CAFE = {
  eyebrow: cafeData.note,
  heading: 'A genuine, healthy & delicious experience',
  body: cafeData.body,
  galleryHeading: 'Inside the café',
  followEyebrow: 'Get Social',
  followHeading: 'Follow the café',
  followBody: 'For daily specials, events and a peek inside the St Kilda café.',
};

const DEFAULT_ABOUT = {
  nutMilksHeading: 'Our Nut Milks',
  nutMilksPara1: 'Al Naturale was born to offer the finest plant-based milks in Melbourne, providing a genuinely natural taste without any artificial additives or preservatives. Our mission is to serve those who are mindful of their dietary choices and seek authentic flavour and true enjoyment in their alternative milk options.',
  nutMilksPara2: 'We are committed to delivering the highest quality plant-based milks by making sustainable and ethical choices, such as packaging each milk in eco-friendly glass bottles. Our approach not only supports the environment but also nourishes your body, recognising the incredible work it does every day.',
  nutMilksImage: 'https://customer-assets-0z36b82j.emergentagent.net/job_fresh-italiano/artifacts/vpublyab_image.png',
  founderHeading: 'About our Founder',
  founderQuote1: '“Coming from a little town called Monopoli in the south of Italy (Puglia), I was always surrounded by the gifts of mother nature. Crystal clear ocean, a sun that warms up the soul, majestic century’s trees, fresh local produce, the smell of the food as you are walking by, and this is how you will end up having the best moments of your life that are so memorable and alive within you, no matter how many years pass by”',
  founderQuote2: 'Marialuisa believes in the value of eating real food and being present. She says that these important cornerstones are what shapes our relationship with food. Give yourself the best gift you can, time. Take the time to enjoy what you truly love.',
  founderImage: aboutData.founderImage,
  quote: aboutData.quote,
  quoteBy: aboutData.quoteBy,
  valuesEyebrow: 'What we stand for',
  valuesHeading: 'Our promise in every bottle',
  values: aboutData.values.map((v) => ({ title: v.title, body: v.body, icon: v.icon })),
  recipeEyebrow: "How it's made",
  recipeHeading: recipeData.title,
  recipeBody: recipeData.body,
  recipeImage: '',
  steps: recipeData.steps.map((s) => ({ n: s.n, t: s.t, d: s.d })),
  ctaHeading: 'Come and taste the story',
  ctaBody: 'Visit us on Acland Street, or discover our range and bring the Al Naturale experience home.',
  ctaBtn1: 'Visit the cafe',
  ctaBtn2: 'Shop the range',
};

const DEFAULT_CATERING = [
  { name: 'Plain Croissants', desc: 'Buttery, flaky and baked fresh, perfect by the dozen.', img: 'https://images.unsplash.com/photo-1623334044303-241021148842?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Filled Savoury Croissants', desc: 'Generously filled with seasonal savoury combinations.', img: 'https://images.unsplash.com/photo-1639667852145-466e29aa49fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Sausage Rolls', desc: 'Golden, hand-rolled and always a crowd favourite.', img: 'https://images.unsplash.com/photo-1608582037152-adefa9decb70?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Pies', desc: 'Hearty house-made pies with rich, slow-cooked fillings.', img: 'https://images.unsplash.com/photo-1545668855-b923f0176935?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Cookies', desc: 'Chewy, wholesome cookies made in small batches.', img: 'https://images.unsplash.com/photo-1583743089695-4b816a340f82?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Brownies / Slices', desc: 'Decadent brownies and rotating seasonal slices.', img: 'https://images.unsplash.com/photo-1515037893149-de7f840978e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Focaccia Varieties', desc: 'Pillowy focaccia in a range of toppings and flavours.', img: 'https://images.unsplash.com/photo-1767065886868-c4ec499606a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
];

const PAGE_TEXT = [
  { page: 'menu', label: 'Menu', fields: [
    { key: 'heading', label: 'Page heading', def: 'Our Café Menu' },
  ]},
  { page: 'products', label: 'Products', fields: [
    { key: 'pickupHeading', label: 'Pick-up box — heading', def: 'Your pick-up box' },
    { key: 'pickupBody', label: 'Pick-up box — body', def: "Add your bottles above and we'll have them ready to collect from the St Kilda cafe.", multi: true },
  ]},
  { page: 'whereToBuy', label: 'Where to Buy', fields: [
    { key: 'wholesaleHeading', label: 'Wholesale banner — heading', def: 'Wholesale & partnerships' },
    { key: 'wholesaleBody', label: 'Wholesale banner — body', def: 'Run a café, grocer or restaurant? Stock the freshest nut milks in Australia. We work hand-in-hand with local partners who care about quality as much as we do.', multi: true },
  ]},
  { page: 'careers', label: 'Careers', fields: [
    { key: 'introHeading', label: 'Intro heading', def: 'Grow with Al Naturale' },
    { key: 'applyHeading', label: 'Apply banner — heading', def: 'Apply to find out current openings' },
  ]},
  { page: 'catering', label: 'Catering', fields: [
    { key: 'menuHeading', label: 'Menu heading', def: 'Our catering menu' },
    { key: 'menuIntro', label: 'Menu intro', def: 'Available in platters and by the dozen. Mix and match to suit your event.', multi: true },
    { key: 'enquiryHeading', label: 'Enquiry banner — heading', def: 'Catering enquiry / order' },
    { key: 'enquiryBody', label: 'Enquiry banner — body', def: "Add quantities for anything you'd like and share your event details, we'll confirm availability, pricing and pick-up or delivery.", multi: true },
  ]},
  { page: 'faq', label: 'FAQ', fields: [
    { key: 'ctaHeading', label: 'Bottom CTA — heading', def: 'Still have a question?' },
  ]},
  { page: 'blog', label: 'Blog', fields: [
    { key: 'allHeading', label: 'All articles — heading', def: 'All articles' },
  ]},
];

const IMAGE_SLOTS = [
  { key: 'hero', label: 'Home — hero background', def: defaultImages.hero },
  { key: 'pour', label: 'Home & About — milk pour photo', def: defaultImages.pour },
  { key: 'cafeInterior', label: 'Café gallery & Home — interior', def: defaultImages.cafeInterior },
  { key: 'cafeTable', label: 'Café gallery — table', def: defaultImages.cafeTable },
  { key: 'cafeCroissant', label: 'Café gallery — croissant', def: defaultImages.cafeCroissant },
  { key: 'cafeStorefront', label: 'Café gallery — storefront', def: defaultImages.cafeStorefront },
];

const emptyStockist = () => ({ id: `loc-${Date.now()}`, name: '', address: '', phone: '', lat: -37.86, lng: 144.98, type: 'Stockist' });
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function defaultSlots() {
  const slots = [];
  for (let h = 7; h <= 14; h += 1) {
    for (const m of [0, 30]) {
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'am' : 'pm';
      slots.push(`${hour12}:${m === 0 ? '00' : '30'}${ampm}`);
    }
  }
  return slots;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('Home Page');
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState('client');
  const [mustChange, setMustChange] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate('/admin/login'); return; }
    (async () => {
      try {
        const info = await me();
        setRole(info.role || 'client');
        setMustChange(!!info.must_change_password);
        const d = await getDraft();
        if (!d.stockists) d.stockists = defaultLocations.map((l) => ({ ...l }));
        if (!d.text) d.text = {};
        HOME_SECTIONS.forEach((grp) => grp.fields.forEach((f) => { if (d.text[f.key] === undefined || d.text[f.key] === '') d.text[f.key] = f.def || ''; }));
        if (!d.products) {
          d.products = defaultProducts.map((p) => ({
            id: p.id, name: p.name, badge: p.badge, tagline: p.tagline,
            description: p.description, tastingNotes: p.tastingNotes, image: p.image,
            notes: [...(p.notes || [])], sizes: (p.sizes || []).map((s) => ({ ...s })),
          }));
        }
        if (!d.menu) d.menu = { hours: defMenuHours, legend: defMenuLegend, sections: JSON.parse(JSON.stringify(defMenuSections)) };
        if (!d.pickup) d.pickup = { cutoffHour: 16, cutoffNote: 'Please place your order by 4:00pm the day before so we can have your freshly made nut milk ready for collection in store.', days: [...WEEKDAYS], slots: defaultSlots() };
        if (!d.jobs) d.jobs = [];
        if (!d.events) d.events = [];
        if (!d.catering) d.catering = DEFAULT_CATERING.map((c) => ({ ...c }));
        if (!d.faqs) d.faqs = JSON.parse(JSON.stringify(defFaqs));
        if (!d.posts) d.posts = defPosts.map((p) => ({ id: p.id, title: p.title, tag: p.tag, read: p.read, date: p.date, excerpt: p.excerpt, image: p.image }));
        if (!d.pages) d.pages = {};
        if (!d.about) d.about = JSON.parse(JSON.stringify(DEFAULT_ABOUT));
        if (!d.cafe) d.cafe = { ...DEFAULT_CAFE };
        PAGE_TEXT.forEach((grp) => {
          d.pages[grp.page] = d.pages[grp.page] || {};
          grp.fields.forEach((f) => { if (d.pages[grp.page][f.key] === undefined || d.pages[grp.page][f.key] === '') d.pages[grp.page][f.key] = f.def || ''; });
        });
        setDraft(d);
      } catch {
        clearToken();
        navigate('/admin/login');
      }
    })();
  }, [navigate]);

  const flash = (msg) => { setStatus(msg); setTimeout(() => setStatus(''), 2500); };

  const doSave = async () => {
    setBusy(true);
    try { await saveDraft(draft); flash('Draft saved'); } catch { flash('Save failed'); } finally { setBusy(false); }
  };
  const doPublish = async () => {
    setBusy(true);
    try { await saveDraft(draft); await publish(); flash('Published live!'); } catch { flash('Publish failed'); } finally { setBusy(false); }
  };
  const logout = () => { clearToken(); navigate('/admin/login'); };

  if (!draft) return <div className="min-h-screen flex items-center justify-center bg-[#E9EBED] text-[#2C3A28]">Loading…</div>;

  if (mustChange) {
    return <ForceChangePassword onDone={() => { setMustChange(false); flash('Password updated'); }} onLogout={logout} />;
  }

  const visibleTabs = role === 'master' ? [...TABS.filter((t) => t !== 'Account'), 'Activity Log', 'Account'] : TABS;

  return (
    <div className="min-h-screen bg-[#E9EBED] text-[#2C3A28]" data-testid="admin-dashboard">
      <div className="sticky top-0 z-20 bg-[#2C3A28] text-white px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={logoCream} alt="Al Naturale" className="h-12 w-auto shrink-0" />
          <span className="font-body text-sm font-semibold text-[#EDEAE0]/90 truncate hidden sm:inline" data-testid="admin-panel-title">Content Management System - Admin Panel</span>
          <span className="shrink-0 rounded-full bg-[#EDEAE0]/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#EDEAE0]" data-testid="admin-role-badge">
            {role === 'master' ? 'Master' : 'Content Editor'}
          </span>
        </div>
        <div className="flex-1" />
        {status && <span className="text-sm text-[#EDEAE0]" data-testid="admin-status">{status}</span>}
        <a href="/" target="_blank" rel="noreferrer" className="text-sm underline underline-offset-4">Preview site</a>
        <button onClick={doSave} disabled={busy} data-testid="save-draft-btn" className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold hover:bg-white/25">Save draft</button>
        <button onClick={doPublish} disabled={busy} data-testid="publish-btn" className="rounded-full bg-[#EDEAE0] text-[#2C3A28] px-4 py-2 text-sm font-semibold hover:bg-white">Publish</button>
        <button onClick={logout} data-testid="logout-btn" className="text-sm text-white/80 hover:text-white">Logout</button>
      </div>

      <div className="px-4 sm:px-6 pt-5 flex gap-2 flex-wrap items-center">
        {visibleTabs.filter((t) => t !== 'Activity Log' && t !== 'Account').map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.replace(/\s+/g,'-').toLowerCase()}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === t ? 'bg-[#2C3A28] text-white' : 'bg-white text-[#2C3A28]'}`}>{t}</button>
        ))}
        <div className="ml-auto flex gap-2 items-center pl-3 border-l border-[#2C3A28]/15">
          {visibleTabs.filter((t) => t === 'Activity Log' || t === 'Account').map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.replace(/\s+/g,'-').toLowerCase()}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === t ? 'bg-[#2C3A28] text-white' : 'bg-white text-[#2C3A28]'}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6 max-w-4xl">
        {tab === 'Stockists' && <StockistsEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Products' && <ProductsEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Menu' && <MenuEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Pickup' && <PickupEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Catering' && <CateringEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Jobs' && <JobsEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Gift Voucher - Orders' && <GiftCardsPanel />}
        {tab === 'Events' && <EventsEditor draft={draft} setDraft={setDraft} />}
        {tab === 'FAQ' && <FaqEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Blog' && <BlogEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Home Page' && <TextEditor draft={draft} setDraft={setDraft} />}
        {tab === 'About' && <AboutEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Our Café' && <CafeEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Page Text' && <PageTextEditor draft={draft} setDraft={setDraft} />}
        {tab === 'Account' && <AccountEditor flash={flash} role={role} />}
        {tab === 'Activity Log' && <ActivityLogPanel />}
      </div>
    </div>
  );
}

const field = "w-full rounded-xl border border-[#2C3A28]/15 bg-white px-3 py-2 text-[#2C3A28] outline-none focus:border-[#2C3A28]";
const labelCls = "block text-xs font-semibold uppercase tracking-wide";

function StockistsEditor({ draft, setDraft }) {
  const list = draft.stockists || [];
  const update = (i, key, val) => {
    const next = list.map((s, idx) => (idx === i ? { ...s, [key]: key === 'lat' || key === 'lng' ? parseFloat(val) || 0 : val } : s));
    setDraft({ ...draft, stockists: next });
  };
  const remove = (i) => setDraft({ ...draft, stockists: list.filter((_, idx) => idx !== i) });
  const add = () => setDraft({ ...draft, stockists: [emptyStockist(), ...list] });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Stockists ({list.length})</h2>
        <button onClick={add} data-testid="add-stockist-btn" className="rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold">+ Add stockist</button>
      </div>
      <div className="mt-4 space-y-4">
        {list.map((s, i) => (
          <div key={s.id || i} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`stockist-row-${i}`}>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className={labelCls}>Name<input className={field} value={s.name || ''} onChange={(e) => update(i, 'name', e.target.value)} /></label>
              <label className={labelCls}>Phone<input className={field} value={s.phone || ''} onChange={(e) => update(i, 'phone', e.target.value)} /></label>
              <label className={`${labelCls} sm:col-span-2`}>Address<input className={field} value={s.address || ''} onChange={(e) => update(i, 'address', e.target.value)} /></label>
              <label className={labelCls}>Map pin — latitude<input className={field} type="number" step="0.0001" value={s.lat ?? ''} onChange={(e) => update(i, 'lat', e.target.value)} /></label>
              <label className={labelCls}>Map pin — longitude<input className={field} type="number" step="0.0001" value={s.lng ?? ''} onChange={(e) => update(i, 'lng', e.target.value)} /></label>
            </div>
            <button onClick={() => remove(i)} data-testid={`remove-stockist-${i}`} className="mt-3 text-sm font-semibold text-red-700 hover:underline">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductsEditor({ draft, setDraft }) {
  const list = draft.products || [];
  const upd = (i, patch) => setDraft({ ...draft, products: list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) });
  const updSize = (i, si, key, val) => {
    const p = list[i];
    const sizes = (p.sizes || []).map((s, idx) => (idx === si ? { ...s, [key]: key === 'price' ? parseFloat(val) || 0 : val } : s));
    upd(i, { sizes });
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">Products ({list.length})</h2>
      <div className="mt-4 space-y-5">
        {list.map((p, i) => (
          <div key={p.id} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`product-row-${p.id}`}>
            <p className="font-semibold text-[#2C3A28]">{p.name} · {p.badge}</p>
            <div className="mt-3">
              <DragDropImage value={p.image} onChange={(url) => upd(i, { image: url })} label="Product image" testid={`product-image-${p.id}`} heightClass="h-48" />
            </div>
            <div className="mt-3 space-y-3">
              <label className={labelCls}>Tagline<input className={field} value={p.tagline || ''} onChange={(e) => upd(i, { tagline: e.target.value })} data-testid={`product-tagline-${p.id}`} /></label>
              <label className={labelCls}>Description<textarea rows={4} className={field} value={p.description || ''} onChange={(e) => upd(i, { description: e.target.value })} data-testid={`product-desc-${p.id}`} /></label>
              <label className={labelCls}>Tasting notes<textarea rows={2} className={field} value={p.tastingNotes || ''} onChange={(e) => upd(i, { tastingNotes: e.target.value })} data-testid={`product-tasting-${p.id}`} /></label>
              <label className={labelCls}>Feature points (one per line)
                <textarea rows={4} className={field} value={(p.notes || []).join('\n')} onChange={(e) => upd(i, { notes: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} data-testid={`product-notes-${p.id}`} />
              </label>
              <div>
                <p className={labelCls}>Sizes &amp; prices</p>
                <div className="mt-2 space-y-2">
                  {(p.sizes || []).map((s, si) => (
                    <div key={si} className="flex gap-3 items-center">
                      <input className={field} value={s.label || ''} onChange={(e) => updSize(i, si, 'label', e.target.value)} data-testid={`product-size-label-${p.id}-${si}`} />
                      <div className="flex items-center gap-1">
                        <span className="text-[#2C3A28]">$</span>
                        <input type="number" step="0.01" className={field} value={s.price ?? ''} onChange={(e) => updSize(i, si, 'price', e.target.value)} data-testid={`product-size-price-${p.id}-${si}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MenuEditor({ draft, setDraft }) {
  const menu = draft.menu || { sections: [] };
  const setMenu = (m) => setDraft({ ...draft, menu: m });
  const sections = menu.sections || [];
  const updMeta = (key, val) => setMenu({ ...menu, [key]: val });
  const updSection = (si, patch) => setMenu({ ...menu, sections: sections.map((s, i) => (i === si ? { ...s, ...patch } : s)) });
  const removeSection = (si) => setMenu({ ...menu, sections: sections.filter((_, i) => i !== si) });
  const addSection = () => setMenu({ ...menu, sections: [...sections, { title: 'New section', items: [] }] });
  const updItem = (si, ii, patch) => updSection(si, { items: sections[si].items.map((it, i) => (i === ii ? { ...it, ...patch } : it)) });
  const removeItem = (si, ii) => updSection(si, { items: sections[si].items.filter((_, i) => i !== ii) });
  const addItem = (si) => updSection(si, { items: [...sections[si].items, { name: '', desc: '', price: '' }] });

  const takeaway = menu.takeaway || defTakeaway;
  const setTakeaway = (t) => setMenu({ ...menu, takeaway: t });
  const taItems = takeaway.items || [];
  const taBundles = takeaway.bundles || [];
  const updTaItem = (i, patch) => setTakeaway({ ...takeaway, items: taItems.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  const removeTaItem = (i) => setTakeaway({ ...takeaway, items: taItems.filter((_, idx) => idx !== i) });
  const addTaItem = () => setTakeaway({ ...takeaway, items: [...taItems, { name: '', tags: '' }] });
  const updTaBundle = (i, patch) => setTakeaway({ ...takeaway, bundles: taBundles.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });
  const removeTaBundle = (i) => setTakeaway({ ...takeaway, bundles: taBundles.filter((_, idx) => idx !== i) });
  const addTaBundle = () => setTakeaway({ ...takeaway, bundles: [...taBundles, { qty: '', price: '' }] });

  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">Café Menu</h2>
      <div className="mt-4 space-y-3">
        <label className={labelCls}>Opening hours line<input className={field} value={menu.hours || ''} onChange={(e) => updMeta('hours', e.target.value)} data-testid="menu-hours" /></label>
        <label className={labelCls}>Legend / footnote<textarea rows={2} className={field} value={menu.legend || ''} onChange={(e) => updMeta('legend', e.target.value)} data-testid="menu-legend" /></label>
      </div>

      {/* Take Away Meals Specials editor */}
      <div className="mt-6 rounded-2xl bg-white p-5 shadow-sm" data-testid="takeaway-specials-editor">
        <h3 className="font-display text-xl font-semibold text-[#2C3A28]">Take Away Meals Specials</h3>
        <p className="mt-1 text-sm text-[#2C3A28]/60">Shown at the top of the menu page. Leave items empty to hide the whole block.</p>
        <label className={`${labelCls} mt-3`}>Title<input className={field} value={takeaway.title || ''} onChange={(e) => setTakeaway({ ...takeaway, title: e.target.value })} data-testid="takeaway-title" /></label>

        <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#2C3A28]/70">Items</p>
        <div className="mt-2 space-y-2">
          {taItems.map((it, i) => (
            <div key={i} className="grid grid-cols-[2fr_2fr_auto] gap-2 items-center">
              <input className={field} placeholder="Item name" value={it.name || ''} onChange={(e) => updTaItem(i, { name: e.target.value })} data-testid={`takeaway-item-name-${i}`} />
              <input className={field} placeholder="Tags (optional) e.g. Vegetarian · Gluten free" value={it.tags || ''} onChange={(e) => updTaItem(i, { tags: e.target.value })} data-testid={`takeaway-item-tags-${i}`} />
              <button onClick={() => removeTaItem(i)} className="text-xs font-semibold text-red-700 hover:underline px-2">Remove</button>
            </div>
          ))}
        </div>
        <button onClick={addTaItem} data-testid="add-takeaway-item" className="mt-2 text-sm font-semibold text-[#2C3A28] hover:underline">+ Add item</button>

        <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#2C3A28]/70">Price bundles</p>
        <div className="mt-2 space-y-2">
          {taBundles.map((b, i) => (
            <div key={i} className="grid grid-cols-[2fr_1fr_auto] gap-2 items-center">
              <input className={field} placeholder="Label e.g. 1 for" value={b.qty || ''} onChange={(e) => updTaBundle(i, { qty: e.target.value })} data-testid={`takeaway-bundle-qty-${i}`} />
              <input className={field} placeholder="Price e.g. 18" value={b.price || ''} onChange={(e) => updTaBundle(i, { price: e.target.value })} data-testid={`takeaway-bundle-price-${i}`} />
              <button onClick={() => removeTaBundle(i)} className="text-xs font-semibold text-red-700 hover:underline px-2">Remove</button>
            </div>
          ))}
        </div>
        <button onClick={addTaBundle} data-testid="add-takeaway-bundle" className="mt-2 text-sm font-semibold text-[#2C3A28] hover:underline">+ Add price bundle</button>
      </div>

      <button onClick={addSection} data-testid="add-menu-section" className="mt-4 rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold">+ Add section</button>
      <div className="mt-4 space-y-5">
        {sections.map((sec, si) => (
          <div key={si} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`menu-section-row-${si}`}>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className={labelCls}>Section title<input className={field} value={sec.title || ''} onChange={(e) => updSection(si, { title: e.target.value })} /></label>
              <label className={labelCls}>Section note (optional)<input className={field} value={sec.note || ''} onChange={(e) => updSection(si, { note: e.target.value })} /></label>
            </div>
            <div className="mt-3 space-y-3">
              {(sec.items || []).map((it, ii) => (
                <div key={ii} className="rounded-xl border border-[#2C3A28]/10 p-3">
                  <div className="grid sm:grid-cols-[2fr_1fr_1fr_1fr] gap-2">
                    <input className={field} placeholder="Dish name" value={it.name || ''} onChange={(e) => updItem(si, ii, { name: e.target.value })} data-testid={`menu-item-name-${si}-${ii}`} />
                    <input className={field} placeholder="Tags e.g. VEG · GF" value={it.tags || ''} onChange={(e) => updItem(si, ii, { tags: e.target.value })} />
                    <input className={field} placeholder="Price" value={it.price || ''} onChange={(e) => updItem(si, ii, { price: e.target.value })} data-testid={`menu-item-price-${si}-${ii}`} />
                    <input className={field} placeholder="Price 2 (opt)" value={it.price2 || ''} onChange={(e) => updItem(si, ii, { price2: e.target.value })} />
                  </div>
                  <textarea rows={2} className={`${field} mt-2`} placeholder="Description (optional)" value={it.desc || ''} onChange={(e) => updItem(si, ii, { desc: e.target.value })} />
                  <button onClick={() => removeItem(si, ii)} className="mt-2 text-xs font-semibold text-red-700 hover:underline">Remove dish</button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-4">
              <button onClick={() => addItem(si)} data-testid={`add-menu-item-${si}`} className="text-sm font-semibold text-[#2C3A28] hover:underline">+ Add dish</button>
              <button onClick={() => removeSection(si)} className="text-sm font-semibold text-red-700 hover:underline">Remove section</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PickupEditor({ draft, setDraft }) {
  const pickup = draft.pickup || {};
  const set = (patch) => setDraft({ ...draft, pickup: { ...pickup, ...patch } });
  const days = pickup.days || [];
  const toggleDay = (d) => set({ days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d] });

  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">Pick-up collection</h2>
      <div className="mt-4 space-y-4">
        <label className={labelCls}>Order cut-off hour (24h, e.g. 16 = 4pm the day before)
          <input type="number" min="0" max="23" className={field} value={pickup.cutoffHour ?? 16} onChange={(e) => set({ cutoffHour: parseInt(e.target.value, 10) || 0 })} data-testid="pickup-cutoff-hour" />
        </label>
        <label className={labelCls}>Cut-off note shown to customers
          <textarea rows={2} className={field} value={pickup.cutoffNote || ''} onChange={(e) => set({ cutoffNote: e.target.value })} data-testid="pickup-cutoff-note" />
        </label>
        <div>
          <p className={labelCls}>Collection days</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {WEEKDAYS.map((d) => (
              <button key={d} onClick={() => toggleDay(d)} data-testid={`pickup-day-${d}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${days.includes(d) ? 'bg-[#2C3A28] text-white' : 'bg-white text-[#2C3A28] border border-[#2C3A28]/20'}`}>{d}</button>
            ))}
          </div>
        </div>
        <label className={labelCls}>Time slots (one per line)
          <textarea rows={8} className={field} value={(pickup.slots || []).join('\n')} onChange={(e) => set({ slots: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} data-testid="pickup-slots" />
        </label>
      </div>
    </div>
  );
}

function JobsEditor({ draft, setDraft }) {
  const list = draft.jobs || [];
  const upd = (i, patch) => setDraft({ ...draft, jobs: list.map((j, idx) => (idx === i ? { ...j, ...patch } : j)) });
  const remove = (i) => setDraft({ ...draft, jobs: list.filter((_, idx) => idx !== i) });
  const add = () => setDraft({ ...draft, jobs: [{ id: `job-${Date.now()}`, title: '', type: '', location: '', description: '' }, ...list] });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Job listings ({list.length})</h2>
        <button onClick={add} data-testid="add-job-btn" className="rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold">+ Add job</button>
      </div>
      <p className="mt-2 text-sm text-[#2C3A28]/70">Listings appear in the Current openings section on the Careers page. Remove all to hide the section.</p>
      <div className="mt-4 space-y-4">
        {list.map((j, i) => (
          <div key={j.id || i} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`job-row-${i}`}>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className={labelCls}>Role title<input className={field} value={j.title || ''} onChange={(e) => upd(i, { title: e.target.value })} data-testid={`job-title-${i}`} /></label>
              <label className={labelCls}>Type (e.g. Full-time)<input className={field} value={j.type || ''} onChange={(e) => upd(i, { type: e.target.value })} /></label>
              <label className={labelCls}>Location<input className={field} value={j.location || ''} onChange={(e) => upd(i, { location: e.target.value })} /></label>
            </div>
            <label className={`${labelCls} mt-3 block`}>Description<textarea rows={3} className={field} value={j.description || ''} onChange={(e) => upd(i, { description: e.target.value })} /></label>
            <button onClick={() => remove(i)} data-testid={`remove-job-${i}`} className="mt-3 text-sm font-semibold text-red-700 hover:underline">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsEditor({ draft, setDraft }) {
  const list = draft.events || [];
  const upd = (i, patch) => setDraft({ ...draft, events: list.map((ev, idx) => (idx === i ? { ...ev, ...patch } : ev)) });
  const remove = (i) => setDraft({ ...draft, events: list.filter((_, idx) => idx !== i) });
  const add = () => setDraft({ ...draft, events: [{ id: `ev-${Date.now()}`, title: '', date: '', description: '' }, ...list] });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Upcoming events ({list.length})</h2>
        <button onClick={add} data-testid="add-event-btn" className="rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold">+ Add event</button>
      </div>
      <p className="mt-2 text-sm text-[#2C3A28]/70">Events show on the Classes &amp; Events page. Remove all to show the "coming soon" message.</p>
      <div className="mt-4 space-y-4">
        {list.map((ev, i) => (
          <div key={ev.id || i} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`event-row-${i}`}>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className={labelCls}>Title<input className={field} value={ev.title || ''} onChange={(e) => upd(i, { title: e.target.value })} data-testid={`event-title-${i}`} /></label>
              <label className={labelCls}>Date / time<input className={field} placeholder="e.g. Sat 12 Jul · 10am" value={ev.date || ''} onChange={(e) => upd(i, { date: e.target.value })} /></label>
            </div>
            <label className={`${labelCls} mt-3 block`}>Description<textarea rows={3} className={field} value={ev.description || ''} onChange={(e) => upd(i, { description: e.target.value })} /></label>
            <button onClick={() => remove(i)} data-testid={`remove-event-${i}`} className="mt-3 text-sm font-semibold text-red-700 hover:underline">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const HOME_SECTIONS = [
  { label: 'Hero', fields: [
    { key: 'heroTitle1', label: 'Heading — line 1', def: 'Plant-Based' },
    { key: 'heroTitle2', label: 'Heading — line 2', def: 'Specialty Milks' },
    { key: 'heroSub', label: 'Subheading', def: 'Nut milk made to the highest quality using a large amount of nutritious nuts.', multi: true },
  ] },
  { label: 'Our Range section', fields: [
    { key: 'rangeEyebrow', label: 'Eyebrow', def: 'Our range' },
    { key: 'rangeHeading', label: 'Heading', def: 'Four milks, endlessly pure' },
    { key: 'rangeLink', label: 'Link label', def: 'Build your box' },
  ] },
  { label: 'Café invite', imageSlot: { key: 'experienceBg', label: 'Background image', def: 'https://customer-assets-0z36b82j.emergentagent.net/job_fresh-italiano/artifacts/bu9e9vdu_image.webp' }, fields: [
    { key: 'experienceHeading', label: 'Heading', def: 'Come and experience the story' },
    { key: 'experienceBody', label: 'Body', def: 'Visit our café on Acland Street and discover our range of fresh food and drinks.', multi: true },
    { key: 'experienceBtn', label: 'Button label', def: 'View our menu' },
  ] },
  { label: 'Order online section', fields: [
    { key: 'orderEyebrow', label: 'Eyebrow', def: 'Order online' },
    { key: 'orderHeading1', label: 'Heading — line 1', def: 'Freshly made.' },
    { key: 'orderHeading2', label: 'Heading — line 2', def: 'Ready when you are.' },
    { key: 'orderSub', label: 'Subtext', def: 'All day breakfast or lunch.', multi: true },
    { key: 'orderBtn', label: 'Button label', def: 'Order Online' },
    { key: 'card1Label', label: 'Card 1 — label', def: 'Ready in' },
    { key: 'card1Value', label: 'Card 1 — value', def: '10-15 min' },
    { key: 'card1Sub', label: 'Card 1 — subtext', def: 'for pick-up' },
    { key: 'card2Label', label: 'Card 2 — label', def: 'Delivery' },
    { key: 'card2Value', label: 'Card 2 — value', def: '25-35 min' },
    { key: 'card2Sub', label: 'Card 2 — subtext', def: 'to your door' },
    { key: 'card3Label', label: 'Card 3 — label', def: 'Hours' },
    { key: 'card3Value', label: 'Card 3 — value', def: '7am to 3pm' },
    { key: 'card3Sub', label: 'Card 3 — subtext', def: 'every day' },
  ] },
  { label: 'Reviews section', fields: [
    { key: 'reviewsEyebrow', label: 'Eyebrow', def: 'Loved in Melbourne' },
    { key: 'reviewsHeading', label: 'Heading', def: 'What people are saying' },
  ] },
  { label: 'Specials section', image: true, fields: [
    { key: 'specialsEyebrow', label: 'Eyebrow', def: 'This month' },
    { key: 'specialsHeading', label: 'Heading', def: 'Our latest specials' },
    { key: 'specialsBody', label: 'Body', def: 'Lunch specials, take-home meals, toastie & coffee deals and more. Download our specials booklet, or grab a 10% off voucher to use in the cafe.', multi: true },
    { key: 'specialsBookletBtn', label: 'Booklet button label', def: 'Get my specials booklet' },
    { key: 'specialsVoucherBtn', label: 'Voucher button label', def: 'Get my 10% off voucher' },
  ] },
  { label: 'Blog teaser', fields: [
    { key: 'blogEyebrow', label: 'Eyebrow', def: 'From the journal' },
    { key: 'blogHeading', label: 'Heading', def: 'Welcome to our blog' },
    { key: 'blogLink', label: 'Link label', def: 'Read all' },
  ] },
];

function TextEditor({ draft, setDraft }) {
  const text = draft.text || {};
  const set = (k, v) => setDraft({ ...draft, text: { ...text, [k]: v } });
  const imgs = draft.images || {};
  const setImg = (k, u) => setDraft({ ...draft, images: { ...imgs, [k]: u } });

  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">Home Page</h2>
      <p className="mt-2 text-sm text-[#2C3A28]/70">Every text block and image on the home page. Leave a field blank to keep the current default.</p>
      <div className="mt-5 space-y-6">
        <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid="home-sec-images">
          <h3 className="font-display text-xl font-semibold text-[#2C3A28]">Hero &amp; feature images</h3>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <DragDropImage value={imgs.hero || defaultImages.hero} onChange={(u) => setImg('hero', u)} label="Hero background" testid="home-image-hero" />
            <DragDropImage value={imgs.pour || defaultImages.pour} onChange={(u) => setImg('pour', u)} label="Milk pour photo (Home & About)" testid="home-image-pour" />
          </div>
        </div>
        {HOME_SECTIONS.map((grp) => (
          <div key={grp.label} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`home-sec-${grp.label.replace(/\s+/g, '-').toLowerCase()}`}>
            <h3 className="font-display text-xl font-semibold text-[#2C3A28]">{grp.label}</h3>
            <div className="mt-3 space-y-3">
              {grp.fields.map((f) => (
                <label key={f.key} className={labelCls}>{f.label}
                  {f.multi
                    ? <textarea rows={3} className={field} placeholder={f.def} value={text[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} data-testid={`text-${f.key}`} />
                    : <input className={field} placeholder={f.def} value={text[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} data-testid={`text-${f.key}`} />}
                </label>
              ))}
              {grp.image && (
                <DragDropImage value={text.specialsImage || specialsData.image} onChange={(url) => set('specialsImage', url)} label="Specials image" testid="specials-image-upload" />
              )}
              {grp.imageSlot && (
                <DragDropImage value={imgs[grp.imageSlot.key] || grp.imageSlot.def} onChange={(url) => setImg(grp.imageSlot.key, url)} label={grp.imageSlot.label} testid={`home-image-${grp.imageSlot.key}`} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImagesEditor({ draft, setDraft }) {
  const imgs = draft.images || {};
  const set = (key, url) => setDraft({ ...draft, images: { ...imgs, [key]: url } });
  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">Site Images</h2>
      <p className="mt-2 text-sm text-[#2C3A28]/70">Drag a new photo onto any slot (or click to browse) to replace it across the site. Save draft, then Publish to go live.</p>
      <div className="mt-5 grid sm:grid-cols-2 gap-5">
        {IMAGE_SLOTS.map((s) => (
          <div key={s.key} className="rounded-2xl bg-white p-4 shadow-sm">
            <DragDropImage value={imgs[s.key] || s.def} onChange={(url) => set(s.key, url)} label={s.label} testid={`site-image-${s.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CateringEditor({ draft, setDraft }) {
  const list = draft.catering || [];
  const upd = (i, patch) => setDraft({ ...draft, catering: list.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const remove = (i) => setDraft({ ...draft, catering: list.filter((_, idx) => idx !== i) });
  const add = () => setDraft({ ...draft, catering: [...list, { name: '', desc: '', img: '' }] });

  const cc = draft.customCakes && draft.customCakes.checklist ? draft.customCakes : defaultCustomCakes;
  const setCC = (next) => setDraft({ ...draft, customCakes: next });
  const ccList = cc.checklist || [];
  const updGroup = (gi, patch) => setCC({ ...cc, checklist: ccList.map((g, i) => (i === gi ? { ...g, ...patch } : g)) });
  const removeGroup = (gi) => setCC({ ...cc, checklist: ccList.filter((_, i) => i !== gi) });
  const addGroup = () => setCC({ ...cc, checklist: [...ccList, { group: 'New group', options: [] }] });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Catering cards ({list.length})</h2>
        <button onClick={add} data-testid="add-catering-btn" className="rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold">+ Add card</button>
      </div>
      <div className="mt-4 space-y-4">
        {list.map((c, i) => (
          <div key={i} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`catering-row-${i}`}>
            <div className="grid sm:grid-cols-[1fr_1.3fr] gap-4">
              <DragDropImage value={c.img} onChange={(url) => upd(i, { img: url })} label="Image" testid={`catering-image-${i}`} heightClass="h-40" />
              <div className="space-y-3">
                <label className={labelCls}>Name<input className={field} value={c.name || ''} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`catering-name-${i}`} /></label>
                <label className={labelCls}>Description<textarea rows={3} className={field} value={c.desc || ''} onChange={(e) => upd(i, { desc: e.target.value })} /></label>
              </div>
            </div>
            <button onClick={() => remove(i)} data-testid={`remove-catering-${i}`} className="mt-3 text-sm font-semibold text-red-700 hover:underline">Remove</button>
          </div>
        ))}
      </div>

      {/* Custom Cakes editor */}
      <div className="mt-8 rounded-2xl bg-white p-5 shadow-sm" data-testid="custom-cakes-editor">
        <h3 className="font-display text-xl font-semibold text-[#2C3A28]">Custom Cakes section</h3>
        <p className="mt-1 text-sm text-[#2C3A28]/60">Controls the custom cake enquiry block on the Catering page.</p>
        <label className={`${labelCls} mt-3`}>Heading<input className={field} value={cc.heading || ''} onChange={(e) => setCC({ ...cc, heading: e.target.value })} data-testid="custom-cakes-heading" /></label>
        <label className={`${labelCls} mt-3`}>Intro text<textarea rows={2} className={field} value={cc.intro || ''} onChange={(e) => setCC({ ...cc, intro: e.target.value })} data-testid="custom-cakes-intro" /></label>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2C3A28]/70">Checklist groups</p>
          <button onClick={addGroup} data-testid="add-cake-group" className="rounded-full bg-[#2C3A28] text-white px-3 py-1.5 text-xs font-semibold">+ Add group</button>
        </div>
        <div className="mt-2 space-y-3">
          {ccList.map((g, gi) => (
            <div key={gi} className="rounded-xl border border-[#2C3A28]/10 p-3" data-testid={`cake-group-${gi}`}>
              <div className="flex items-center gap-2">
                <input className={field} value={g.group || ''} onChange={(e) => updGroup(gi, { group: e.target.value })} placeholder="Group name" data-testid={`cake-group-name-${gi}`} />
                <button onClick={() => removeGroup(gi)} className="shrink-0 text-xs font-semibold text-red-700 hover:underline px-2">Remove</button>
              </div>
              <label className={`${labelCls} mt-2 block`}>Options (one per line)
                <textarea
                  rows={Math.max(3, (g.options || []).length)}
                  className={field}
                  value={(g.options || []).join('\n')}
                  onChange={(e) => updGroup(gi, { options: e.target.value.split('\n').map((o) => o.trim()).filter(Boolean) })}
                  data-testid={`cake-group-options-${gi}`}
                />
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FaqEditor({ draft, setDraft }) {
  const cats = draft.faqs || [];
  const setCats = (c) => setDraft({ ...draft, faqs: c });
  const updCat = (ci, patch) => setCats(cats.map((c, i) => (i === ci ? { ...c, ...patch } : c)));
  const removeCat = (ci) => setCats(cats.filter((_, i) => i !== ci));
  const addCat = () => setCats([...cats, { id: `cat-${Date.now()}`, label: 'New category', items: [] }]);
  const updItem = (ci, ii, patch) => updCat(ci, { items: cats[ci].items.map((it, i) => (i === ii ? { ...it, ...patch } : it)) });
  const removeItem = (ci, ii) => updCat(ci, { items: cats[ci].items.filter((_, i) => i !== ii) });
  const addItem = (ci) => updCat(ci, { items: [...cats[ci].items, { q: '', a: '' }] });
  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">FAQ ({cats.length} categories)</h2>
        <button onClick={addCat} data-testid="add-faq-cat-btn" className="rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold">+ Add category</button>
      </div>
      <div className="mt-4 space-y-5">
        {cats.map((cat, ci) => (
          <div key={cat.id || ci} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`faq-cat-${ci}`}>
            <label className={labelCls}>Category label<input className={field} value={cat.label || ''} onChange={(e) => updCat(ci, { label: e.target.value })} /></label>
            <div className="mt-3 space-y-3">
              {(cat.items || []).map((it, ii) => (
                <div key={ii} className="rounded-xl border border-[#2C3A28]/10 p-3">
                  <label className={labelCls}>Question<input className={field} value={it.q || ''} onChange={(e) => updItem(ci, ii, { q: e.target.value })} data-testid={`faq-q-${ci}-${ii}`} /></label>
                  <label className={`${labelCls} mt-2 block`}>Answer<textarea rows={3} className={field} value={it.a || ''} onChange={(e) => updItem(ci, ii, { a: e.target.value })} /></label>
                  <button onClick={() => removeItem(ci, ii)} className="mt-2 text-xs font-semibold text-red-700 hover:underline">Remove question</button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-4">
              <button onClick={() => addItem(ci)} data-testid={`add-faq-item-${ci}`} className="text-sm font-semibold text-[#2C3A28] hover:underline">+ Add question</button>
              <button onClick={() => removeCat(ci)} className="text-sm font-semibold text-red-700 hover:underline">Remove category</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogEditor({ draft, setDraft }) {
  const list = draft.posts || [];
  const upd = (i, patch) => setDraft({ ...draft, posts: list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) });
  const remove = (i) => setDraft({ ...draft, posts: list.filter((_, idx) => idx !== i) });
  const add = () => setDraft({ ...draft, posts: [{ id: `post-${Date.now()}`, title: '', tag: '', read: '3 min', date: '', excerpt: '', image: '' }, ...list] });
  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Blog posts ({list.length})</h2>
        <button onClick={add} data-testid="add-post-btn" className="rounded-full bg-[#2C3A28] text-white px-4 py-2 text-sm font-semibold">+ Add post</button>
      </div>
      <p className="mt-2 text-sm text-[#2C3A28]/70">The first post is shown as the featured story.</p>
      <div className="mt-4 space-y-4">
        {list.map((p, i) => (
          <div key={p.id || i} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`post-row-${i}`}>
            <div className="grid sm:grid-cols-[1fr_1.3fr] gap-4">
              <DragDropImage value={p.image} onChange={(url) => upd(i, { image: url })} label="Cover image" testid={`post-image-${i}`} heightClass="h-40" />
              <div className="space-y-3">
                <label className={labelCls}>Title<input className={field} value={p.title || ''} onChange={(e) => upd(i, { title: e.target.value })} data-testid={`post-title-${i}`} /></label>
                <div className="grid grid-cols-3 gap-2">
                  <label className={labelCls}>Tag<input className={field} value={p.tag || ''} onChange={(e) => upd(i, { tag: e.target.value })} /></label>
                  <label className={labelCls}>Read time<input className={field} value={p.read || ''} onChange={(e) => upd(i, { read: e.target.value })} /></label>
                  <label className={labelCls}>Date<input className={field} value={p.date || ''} onChange={(e) => upd(i, { date: e.target.value })} /></label>
                </div>
                <label className={labelCls}>Excerpt<textarea rows={3} className={field} value={p.excerpt || ''} onChange={(e) => upd(i, { excerpt: e.target.value })} /></label>
              </div>
            </div>
            <button onClick={() => remove(i)} data-testid={`remove-post-${i}`} className="mt-3 text-sm font-semibold text-red-700 hover:underline">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PageTextEditor({ draft, setDraft }) {
  const pages = draft.pages || {};
  const set = (page, key, val) => setDraft({ ...draft, pages: { ...pages, [page]: { ...(pages[page] || {}), [key]: val } } });
  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">Page Text &amp; Headings</h2>
      <p className="mt-2 text-sm text-[#2C3A28]/70">Edit headings and copy across every page. Leave blank to keep the current default.</p>
      <div className="mt-5 space-y-6">
        {PAGE_TEXT.map((grp) => (
          <div key={grp.page} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`pagetext-${grp.page}`}>
            <h3 className="font-display text-xl font-semibold text-[#2C3A28]">{grp.label}</h3>
            <div className="mt-3 space-y-3">
              {grp.fields.map((f) => (
                <label key={f.key} className={labelCls}>{f.label}
                  {f.multi
                    ? <textarea rows={3} className={field} placeholder={f.def} value={(pages[grp.page] || {})[f.key] ?? ''} onChange={(e) => set(grp.page, f.key, e.target.value)} data-testid={`pt-${grp.page}-${f.key}`} />
                    : <input className={field} placeholder={f.def} value={(pages[grp.page] || {})[f.key] ?? ''} onChange={(e) => set(grp.page, f.key, e.target.value)} data-testid={`pt-${grp.page}-${f.key}`} />}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Card = ({ title, children, testid }) => (
  <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid={testid}>
    <h3 className="font-display text-xl font-semibold text-[#2C3A28]">{title}</h3>
    <div className="mt-3 space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, multi, tid, rows = 3 }) => (
  <label className={labelCls}>{label}
    {multi
      ? <textarea rows={rows} className={field} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
      : <input className={field} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} />}
  </label>
);

function AboutEditor({ draft, setDraft }) {
  const a = draft.about || {};
  const setA = (patch) => setDraft({ ...draft, about: { ...a, ...patch } });
  const values = a.values || [];
  const steps = a.steps || [];
  const setVal = (i, key, v) => setA({ values: values.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)) });
  const setStep = (i, key, v) => setA({ steps: steps.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)) });

  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">About Page</h2>
      <p className="mt-2 text-sm text-[#2C3A28]/70">Every text block and image on the About page.</p>
      <div className="mt-5 space-y-6">
        <Card title="Our Nut Milks" testid="about-sec-nutmilks">
          <DragDropImage value={a.nutMilksImage} onChange={(u) => setA({ nutMilksImage: u })} label="Image" testid="about-image-nutmilks" />
          <Field label="Heading" value={a.nutMilksHeading} onChange={(v) => setA({ nutMilksHeading: v })} tid="about-nutMilksHeading" />
          <Field label="Paragraph 1" multi rows={4} value={a.nutMilksPara1} onChange={(v) => setA({ nutMilksPara1: v })} />
          <Field label="Paragraph 2" multi rows={4} value={a.nutMilksPara2} onChange={(v) => setA({ nutMilksPara2: v })} />
        </Card>

        <Card title="About our Founder" testid="about-sec-founder">
          <DragDropImage value={a.founderImage} onChange={(u) => setA({ founderImage: u })} label="Founder photo" testid="about-image-founder" />
          <Field label="Heading" value={a.founderHeading} onChange={(v) => setA({ founderHeading: v })} tid="about-founderHeading" />
          <Field label="Quote 1" multi rows={5} value={a.founderQuote1} onChange={(v) => setA({ founderQuote1: v })} />
          <Field label="Quote 2" multi rows={4} value={a.founderQuote2} onChange={(v) => setA({ founderQuote2: v })} />
        </Card>

        <Card title="Pull quote" testid="about-sec-quote">
          <Field label="Quote" multi value={a.quote} onChange={(v) => setA({ quote: v })} />
          <Field label="Attribution" value={a.quoteBy} onChange={(v) => setA({ quoteBy: v })} />
        </Card>

        <Card title="What we stand for (values)" testid="about-sec-values">
          <Field label="Eyebrow" value={a.valuesEyebrow} onChange={(v) => setA({ valuesEyebrow: v })} />
          <Field label="Heading" value={a.valuesHeading} onChange={(v) => setA({ valuesHeading: v })} />
          <div className="space-y-3">
            {values.map((v, i) => (
              <div key={i} className="rounded-xl border border-[#2C3A28]/10 p-3" data-testid={`about-value-${i}`}>
                <Field label={`Value ${i + 1} — title`} value={v.title} onChange={(val) => setVal(i, 'title', val)} />
                <div className="mt-2"><Field label="Body" multi value={v.body} onChange={(val) => setVal(i, 'body', val)} /></div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="How it's made" testid="about-sec-recipe">
          <DragDropImage value={a.recipeImage} onChange={(u) => setA({ recipeImage: u })} label="Photo (defaults to the milk pour image)" testid="about-image-recipe" />
          <Field label="Eyebrow" value={a.recipeEyebrow} onChange={(v) => setA({ recipeEyebrow: v })} />
          <Field label="Heading" value={a.recipeHeading} onChange={(v) => setA({ recipeHeading: v })} tid="about-recipeHeading" />
          <Field label="Body" multi value={a.recipeBody} onChange={(v) => setA({ recipeBody: v })} />
          <div className="space-y-3">
            {steps.map((s, i) => (
              <div key={i} className="rounded-xl border border-[#2C3A28]/10 p-3" data-testid={`about-step-${i}`}>
                <div className="grid grid-cols-[80px_1fr] gap-2">
                  <Field label="No." value={s.n} onChange={(val) => setStep(i, 'n', val)} />
                  <Field label="Title" value={s.t} onChange={(val) => setStep(i, 't', val)} />
                </div>
                <div className="mt-2"><Field label="Description" multi value={s.d} onChange={(val) => setStep(i, 'd', val)} /></div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Call to action band" testid="about-sec-cta">
          <Field label="Heading" value={a.ctaHeading} onChange={(v) => setA({ ctaHeading: v })} tid="about-ctaHeading" />
          <Field label="Body" multi value={a.ctaBody} onChange={(v) => setA({ ctaBody: v })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Button 1 label" value={a.ctaBtn1} onChange={(v) => setA({ ctaBtn1: v })} />
            <Field label="Button 2 label" value={a.ctaBtn2} onChange={(v) => setA({ ctaBtn2: v })} />
          </div>
        </Card>
      </div>
    </div>
  );
}

function CafeEditor({ draft, setDraft }) {
  const c = draft.cafe || {};
  const setC = (patch) => setDraft({ ...draft, cafe: { ...c, ...patch } });
  const imgs = draft.images || {};
  const setImg = (k, u) => setDraft({ ...draft, images: { ...imgs, [k]: u } });

  return (
    <div>
      <h2 className="font-display text-2xl font-semibold">Our Café Page</h2>
      <p className="mt-2 text-sm text-[#2C3A28]/70">Every text block and image on the Café page.</p>
      <div className="mt-5 space-y-6">
        <Card title="Intro" testid="cafe-sec-intro">
          <Field label="Eyebrow" value={c.eyebrow} onChange={(v) => setC({ eyebrow: v })} tid="cafe-eyebrow" />
          <Field label="Heading" value={c.heading} onChange={(v) => setC({ heading: v })} tid="cafe-heading" />
          <Field label="Body" multi rows={4} value={c.body} onChange={(v) => setC({ body: v })} tid="cafe-body" />
        </Card>

        <Card title="Gallery" testid="cafe-sec-gallery">
          <Field label="Gallery heading" value={c.galleryHeading} onChange={(v) => setC({ galleryHeading: v })} tid="cafe-galleryHeading" />
          <div className="grid sm:grid-cols-2 gap-4">
            <DragDropImage value={imgs.cafeInterior || defaultImages.cafeInterior} onChange={(u) => setImg('cafeInterior', u)} label="Interior" testid="cafe-image-cafeInterior" />
            <DragDropImage value={imgs.cafeTable || defaultImages.cafeTable} onChange={(u) => setImg('cafeTable', u)} label="Table" testid="cafe-image-cafeTable" />
            <DragDropImage value={imgs.cafeCroissant || defaultImages.cafeCroissant} onChange={(u) => setImg('cafeCroissant', u)} label="Croissant" testid="cafe-image-cafeCroissant" />
            <DragDropImage value={imgs.cafeStorefront || defaultImages.cafeStorefront} onChange={(u) => setImg('cafeStorefront', u)} label="Storefront" testid="cafe-image-cafeStorefront" />
          </div>
        </Card>

        <Card title="Follow the café panel" testid="cafe-sec-follow">
          <Field label="Eyebrow" value={c.followEyebrow} onChange={(v) => setC({ followEyebrow: v })} />
          <Field label="Heading" value={c.followHeading} onChange={(v) => setC({ followHeading: v })} tid="cafe-followHeading" />
          <Field label="Body" multi value={c.followBody} onChange={(v) => setC({ followBody: v })} />
        </Card>
      </div>
    </div>
  );
}

function GiftCardsPanel() {
  const [cards, setCards] = useState(null);
  const [edits, setEdits] = useState({});
  const [err, setErr] = useState('');
  const load = async () => {
    try { setCards(await listGiftCards()); } catch { setErr('Could not load gift cards'); }
  };
  useEffect(() => { load(); }, []);
  const adjust = async (code, balance) => {
    try { const updated = await adjustGiftCard(code, balance); setCards((cs) => cs.map((c) => (c.code === code ? updated : c))); setEdits((e) => ({ ...e, [code]: undefined })); }
    catch { setErr('Update failed'); }
  };
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '');

  if (cards === null) return <p className="text-[#2C3A28]/60">Loading gift cards…</p>;

  const active = cards.filter((c) => c.status === 'active');
  const outstanding = active.reduce((sum, c) => sum + Number(c.balance), 0);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl font-semibold">Gift Voucher - Orders ({cards.length})</h2>
        <button onClick={load} className="text-sm font-semibold text-[#2C3A28] hover:underline">Refresh</button>
      </div>
      <p className="mt-2 text-sm text-[#2C3A28]/70">{active.length} active · ${outstanding.toFixed(2)} outstanding balance. Enter a new balance after a customer redeems in store, or set it to 0 to mark fully used.</p>
      {err && <p className="mt-2 text-sm text-red-700">{err}</p>}
      {cards.length === 0 ? (
        <p className="mt-6 text-[#2C3A28]/60">No gift cards purchased yet.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {cards.map((c) => (
            <div key={c.code} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`giftcard-row-${c.code}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-display text-xl font-semibold text-[#2C3A28] tracking-wide">{c.code}</span>
                <span className={`text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full ${c.status === 'active' ? 'bg-[#2C3A28] text-[#EDEAE0]' : 'bg-[#2C3A28]/10 text-[#2C3A28]/60'}`}>{c.status === 'active' ? 'Active' : 'Redeemed'}</span>
              </div>
              <div className="mt-2 grid sm:grid-cols-4 gap-2 text-sm text-[#2C3A28]">
                <div><span className="text-[#2C3A28]/50">Value</span><br />${c.amount}</div>
                <div><span className="text-[#2C3A28]/50">Balance</span><br /><strong>${Number(c.balance).toFixed(2)}</strong></div>
                <div><span className="text-[#2C3A28]/50">Buyer</span><br />{c.buyerName || '—'}</div>
                <div><span className="text-[#2C3A28]/50">Recipient</span><br />{c.recipientName || '—'}</div>
              </div>
              {c.message && <p className="mt-2 text-sm text-[#2C3A28]/70 italic">“{c.message}”</p>}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[#2C3A28]/50">{fmtDate(c.created_at)}</span>
                <div className="flex-1" />
                <label className="text-xs font-semibold text-[#2C3A28]">New balance $
                  <input type="number" min="0" max={c.amount} step="0.01"
                    value={edits[c.code] ?? c.balance}
                    data-testid={`giftcard-balance-${c.code}`}
                    onChange={(ev) => setEdits((e) => ({ ...e, [c.code]: ev.target.value }))}
                    onKeyDown={(ev) => { if (ev.key === 'Enter') adjust(c.code, parseFloat(ev.target.value) || 0); }}
                    className="ml-1 w-24 rounded-lg border border-[#2C3A28]/15 px-2 py-1 outline-none focus:border-[#2C3A28]" />
                </label>
                <button onClick={() => adjust(c.code, parseFloat(edits[c.code] ?? c.balance) || 0)} data-testid={`giftcard-save-${c.code}`} className="rounded-full bg-[#2C3A28] text-white px-4 py-1.5 text-xs font-semibold">Update</button>
                <button onClick={() => adjust(c.code, 0)} data-testid={`giftcard-redeem-${c.code}`} className="rounded-full border border-[#2C3A28]/25 text-[#2C3A28] px-4 py-1.5 text-xs font-semibold">Mark fully used</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityLogPanel() {
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    getActivityLog().then(setEntries).catch((e) => { setErr(e.message); setEntries([]); });
  }, []);

  const SECTION_LABELS = {
    home: 'Home Page', text: 'Home Page', pages: 'Page Text', about: 'About', cafe: 'Our Café',
    products: 'Products', menu: 'Menu', pickup: 'Pickup', catering: 'Catering', customCakes: 'Custom Cakes',
    locations: 'Stockists', stockists: 'Stockists', events: 'Events', classes: 'Events', jobs: 'Jobs',
    careers: 'Jobs', blog: 'Blog', faq: 'FAQ', images: 'Images', reviews: 'Reviews',
  };
  const labelFor = (s) => SECTION_LABELS[s] || s;
  const fmt = (ts) => {
    try {
      return new Date(ts).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ts; }
  };

  return (
    <div data-testid="activity-log-panel">
      <h2 className="font-display text-2xl font-semibold">Activity Log</h2>
      <p className="mt-1 text-sm text-[#2C3A28]/60">Every time content is published, it's recorded here with who did it, when, and what changed.</p>
      {err && <p className="mt-3 text-sm text-red-700">{err}</p>}
      {entries === null && <p className="mt-6 text-[#2C3A28]/60">Loading…</p>}
      {entries && entries.length === 0 && !err && <p className="mt-6 text-[#2C3A28]/60">No publish activity recorded yet.</p>}
      <div className="mt-5 space-y-3">
        {entries && entries.map((e) => (
          <div key={e.id} className="rounded-2xl bg-white p-4 shadow-sm flex flex-wrap items-center gap-3" data-testid="activity-log-entry">
            <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${e.role === 'master' ? 'bg-[#2C3A28] text-[#EDEAE0]' : 'bg-[#D2B489] text-[#2C3A28]'}`}>
              {e.role === 'master' ? 'You (Master)' : 'Client'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#2C3A28] truncate">{e.email}</p>
              <p className="text-xs text-[#2C3A28]/60">{fmt(e.timestamp)}</p>
            </div>
            <div className="w-full sm:w-auto sm:max-w-[60%] flex flex-wrap gap-1.5">
              {(e.sections && e.sections.length) ? (
                [...new Set(e.sections.map(labelFor))].map((s) => (
                  <span key={s} className="rounded-md bg-[#EDEAE0] px-2 py-1 text-xs font-medium text-[#2C3A28]">{s}</span>
                ))
              ) : (
                <span className="text-xs text-[#2C3A28]/50">Published (no section changes)</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountEditor({ flash, role }) {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await changePassword(cur, nw); setCur(''); setNw(''); flash('Password updated'); }
    catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg space-y-10">
      <form onSubmit={submit} className="max-w-sm" data-testid="change-password-form">
        <h2 className="font-display text-2xl font-semibold">Change your password</h2>
        <p className="mt-1 text-sm text-[#2C3A28]/60">Signed in as <span className="font-semibold capitalize">{role}</span></p>
        <input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className={`${field} mt-4`} data-testid="cur-pw" required />
        <input type="password" placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} className={`${field} mt-3`} data-testid="new-pw" required minLength={8} />
        {err && <p className="mt-2 text-sm text-red-700">{err}</p>}
        <button type="submit" disabled={busy} data-testid="change-pw-btn" className="mt-4 rounded-full bg-[#2C3A28] text-white px-6 py-3 text-sm font-semibold disabled:opacity-60">Update password</button>
      </form>
      {role === 'master' && <ClientAccountManager />}
    </div>
  );
}

function ClientAccountManager() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [temp, setTemp] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  const client = users.find((u) => u.role === 'client');

  const doReset = async () => {
    if (!client) return;
    if (!window.confirm(`Reset the password for ${client.email}? Their current password will stop working immediately.`)) return;
    setBusy(true); setErr(''); setTemp(null); setCopied(false);
    try {
      const res = await resetUserPassword(client.email);
      setTemp(res.temp_password);
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(temp);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-2xl border border-[#2C3A28]/12 bg-white p-6" data-testid="client-account-manager">
      <h2 className="font-display text-2xl font-semibold">Client account</h2>
      <p className="mt-1 text-sm text-[#2C3A28]/60">Manage the content editor login. Only you (master) can do this.</p>
      {!client && <p className="mt-4 text-sm text-[#2C3A28]/70">No client account found.</p>}
      {client && (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-[#EDEAE0] px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" data-testid="client-email">{client.email}</p>
              <p className="text-xs text-[#2C3A28]/60">
                Role: content editor{client.must_change_password ? ' · awaiting password change' : ''}
              </p>
            </div>
            <button onClick={doReset} disabled={busy} data-testid="reset-client-pw-btn" className="shrink-0 rounded-full bg-[#2C3A28] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
          </div>
          {err && <p className="mt-3 text-sm text-red-700">{err}</p>}
          {temp && (
            <div className="mt-4 rounded-xl border border-[#2C3A28]/15 bg-[#2C3A28] text-[#EDEAE0] p-4" data-testid="temp-password-box">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#EDEAE0]/70">Temporary password</p>
              <p className="mt-1.5 font-mono text-xl font-semibold tracking-wide" data-testid="temp-password-value">{temp}</p>
              <p className="mt-2 text-xs text-[#EDEAE0]/80 leading-relaxed">
                Send this to your client. They sign in with it and will be prompted to set their own new password. Copy it now — it won't be shown again.
              </p>
              <button onClick={copy} data-testid="copy-temp-pw-btn" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#EDEAE0] text-[#2C3A28] px-5 py-2 text-sm font-semibold">
                {copied ? 'Copied' : 'Copy password'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ForceChangePassword({ onDone, onLogout }) {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await changePassword(cur, nw); onDone(); }
    catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#2C3A28] px-5" data-testid="force-change-password">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#EDEAE0] rounded-3xl p-8 shadow-xl">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-[#2C3A28]/60">Al Naturale</p>
        <h1 className="mt-2 text-center font-display text-2xl font-semibold text-[#2C3A28]">Set your new password</h1>
        <p className="mt-2 text-center text-sm text-[#2C3A28]/70">You're using a temporary password. Please choose a new one to continue.</p>
        <input type="password" placeholder="Temporary password" value={cur} onChange={(e) => setCur(e.target.value)} className={`${field} mt-5`} data-testid="force-cur-pw" required />
        <input type="password" placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} className={`${field} mt-3`} data-testid="force-new-pw" required minLength={8} />
        {err && <p className="mt-2 text-sm text-red-700" data-testid="force-pw-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="force-change-pw-btn" className="mt-5 w-full rounded-full bg-[#2C3A28] text-white py-3.5 font-semibold disabled:opacity-60">
          {busy ? 'Saving…' : 'Save and continue'}
        </button>
        <button type="button" onClick={onLogout} className="mt-3 w-full text-sm text-[#2C3A28]/60 hover:text-[#2C3A28]">Log out</button>
      </form>
    </div>
  );
}
